import React from 'react';
import { currencyToCents } from '../../../utils/formatNumber';
import { InputAdornment, TextField } from '@mui/material';
import i18n from '../../../i18n';

/**
 * Money input. Displays major units; stores minor units (cents).
 *
 * The bug this fixes: on invalid input the old version set a local error flag and
 * returned *without* calling onChange. The form therefore kept the last valid
 * amount while the box displayed something else, and since nothing disabled Save,
 * pressing it persisted a number the user could not see. Four of the eight fields
 * on a payin instance form are amounts.
 *
 * Invalid input is now reported upward as-is, so the numeric rule in
 * utils/fieldValidation fails, Save is disabled, and there is no way for a value
 * to be saved that is not the one on screen.
 */
export default class AmountInput extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            inputValue: AmountInput.toDisplay(props.value),
            // What we last handed upward. Used to tell an echo of our own change
            // apart from an external one (reset, template, different record).
            lastEmitted: props.value,
            invalid: false
        };
    }

    static toDisplay(value) {
        if (value === null || value === undefined || value === '') return '';
        const numeric = Number(value);
        // A non-numeric value can only be one we ourselves reported as invalid,
        // so show it back verbatim rather than dividing it by 100.
        return Number.isFinite(numeric) ? numeric / 100 : String(value);
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (nextProps.value === prevState.lastEmitted) return null;

        return {
            inputValue: AmountInput.toDisplay(nextProps.value),
            lastEmitted: nextProps.value,
            invalid: false
        };
    }

    parse = (raw) => {
        const cleaned = String(raw).replace(',', '.').trim();
        if (!/^-?\d*(\.\d{0,2})?$/.test(cleaned)) return { isValid: false };

        const parsed = Number(cleaned);
        if (!Number.isFinite(parsed)) return { isValid: false };

        return { isValid: true, parsed };
    };

    handleChange = (event) => {
        const raw = event.target.value;
        const result = this.parse(raw);
        const emitted = result.isValid ? currencyToCents(result.parsed) : raw;

        this.setState({
            inputValue: raw,
            lastEmitted: emitted,
            invalid: !result.isValid
        });

        this.props.onChange(this.props.id, emitted);
    };

    handleBlur = (event) => {
        const result = this.parse(this.state.inputValue);

        if (result.isValid && this.state.inputValue !== '') {
            this.setState({ inputValue: result.parsed.toFixed(2) });
        }

        this.props.onBlur?.(event);
    };

    render() {
        const { id, label, currencySymbol, error, autoFocus, required, helperText } =
            this.props;
        const { inputValue, invalid } = this.state;

        // Local flag as well as the form's error: it gives immediate feedback on
        // the keystroke, while the form's message only appears once the field has
        // been touched.
        const showError = invalid || Boolean(error);

        return (
            <TextField
                key={id}
                autoFocus={autoFocus}
                id={id}
                label={label}
                required={Boolean(required)}
                placeholder="0.00"
                value={inputValue}
                fullWidth
                error={showError}
                // A validation message replaces the static hint while it is
                // showing, as in the other dialog fields.
                helperText={
                    showError
                        ? error || i18n.t('errors.invalidNumber')
                        : helperText || ''
                }
                onChange={this.handleChange}
                onBlur={this.handleBlur}
                onFocus={(e) => e.target.select()}
                InputProps={{
                    startAdornment: currencySymbol ? (
                        <InputAdornment position="start">
                            {currencySymbol}
                        </InputAdornment>
                    ) : null,
                    inputMode: 'decimal'
                }}
            />
        );
    }
}
